import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { newsArticles } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function NewsList() {
  const [order, setOrder] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    "娱乐资讯 - 芒果tv",
    "芒果tv 娱乐资讯频道，汇集最新剧集、综艺、电影与行业动态，第一时间掌握娱乐热点。"
  );

  const sorted = useMemo(() => {
    return [...newsArticles].sort((a, b) =>
      order === "desc"
        ? a.publishedAt < b.publishedAt
          ? 1
          : -1
        : a.publishedAt > b.publishedAt
        ? 1
        : -1
    );
  }, [order]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const changeOrder = (value: "desc" | "asc") => {
    setOrder(value);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "娱乐资讯" }]} />

      <div className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">娱乐资讯</h1>
          <p className="mt-1 text-sm text-foreground-500">
            最新剧集、综艺、电影与行业动态，一网打尽。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="flex rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => changeOrder("desc")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                order === "desc" ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              最新发布
            </button>
            <button
              type="button"
              onClick={() => changeOrder("asc")}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                order === "asc" ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              最早发布
            </button>
          </div>
        </div>
      </div>

      {current.length > 0 ? (
        <>
          <div className="mt-6 flex flex-col gap-3">
            {current.map((a) => (
              <NewsCard key={a.id} article={a} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 disabled:opacity-40 enabled:cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`h-9 min-w-9 rounded-md px-3 text-sm font-medium transition-colors cursor-pointer ${
                    n === page
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 disabled:opacity-40 enabled:cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-newspaper-line text-3xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
            暂无资讯内容
          </p>
          <p className="mt-1 text-sm text-foreground-500">稍后再来看看吧。</p>
        </div>
      )}
    </div>
  );
}